import React, { useState } from 'react';
import axios from 'axios';
import { SubmissionQueueModal, SubmissionQueueFile } from './ReviewDashboard';
import { detectRevisionType, formatMismatchMessage, reentryText, workingMaterialReminder, startFromWorkingFileHint } from './revisionRules';
import CompanionDecisionModal, { CompanionDecision } from './CompanionDecisionModal';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * In-cycle "Submit revised version" (2026-09-01, Fredrik — testing Producer
 * view surfaced that the re-upload lived only in the My Submissions rows;
 * the producer standing INSIDE the cycle had no way to act on
 * revision_needed). Second mount point of the revision flow; the policy
 * (format must match) has its one home in revisionRules.ts, shared with
 * ProducerDashboard.
 *
 * File cycles pick the fixed file from the submission queue; webpage cycles
 * have no file — the revision is a re-capture of the same URL.
 */
interface SubmitRevisionControlProps {
  cycle: { _id: string; contentItem: { name: string; sourceFileId?: string | null }; contentType?: string | null };
  getAuthHeaders: () => Promise<Record<string, string>>;
  onSubmitted: () => void;
}

const SubmitRevisionControl: React.FC<SubmitRevisionControlProps> = ({ cycle, getAuthHeaders, onSubmitted }) => {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  // pdf-with-companion: the picked pdf waits here for the companion decision
  const [pendingPdf, setPendingPdf] = useState<SubmissionQueueFile | null>(null);
  const isWebpage = cycle.contentType === 'webpage';
  const needsCompanionDecision = cycle.contentType === 'pdf' && !!cycle.contentItem.sourceFileId;

  const post = async (body: Record<string, unknown>) => {
    try {
      setBusy(true);
      const headers = await getAuthHeaders();
      const r = await axios.post(`${backendUrl}/api/content-review/cycles/${cycle._id}/revisions`, body, { headers });
      setOk(true);
      setMsg(`v${r.data?.contentItem?.version || '?'} of "${cycle.contentItem.name}" ${reentryText(r.data?.reentry)}.${isWebpage ? ' The page was re-captured from its URL.' : ''}${workingMaterialReminder(cycle.contentType)}`);
      onSubmitted();
    } catch (err: any) {
      setOk(false);
      setMsg(err.response?.data?.error || 'Failed to submit the revision');
    } finally {
      setBusy(false);
      setPicking(false);
    }
  };

  const pickFile = (file: SubmissionQueueFile) => {
    const picked = detectRevisionType(file.fileName);
    if (cycle.contentType && picked !== cycle.contentType) {
      setOk(false);
      setMsg(formatMismatchMessage(file.fileName, picked, cycle.contentItem.name, cycle.contentType));
      setPicking(false);
      return;
    }
    if (needsCompanionDecision) {
      // COMPANION_DECISION_REQUIRED (backend): the pdf's editable master must
      // be handed over or attested unchanged before the revision is accepted.
      setPendingPdf(file);
      setPicking(false);
      return;
    }
    post({ fileId: file.fileId, originalFileName: file.fileName });
  };

  const decideCompanion = (d: CompanionDecision) => {
    if (!pendingPdf) return;
    const extras = d.kind === 'new'
      ? { sourceFileId: d.fileId, sourceFileName: d.fileName }
      : { sourceUnchanged: true };
    const pdf = pendingPdf;
    setPendingPdf(null);
    post({ fileId: pdf.fileId, originalFileName: pdf.fileName, ...extras });
  };

  return (
    <div data-testid="submit-revision-control" style={{ margin: '8px 0' }}>
      <button
        className="review-btn review-btn-success"
        disabled={busy}
        onClick={() => {
          setMsg(null);
          if (isWebpage) {
            if (window.confirm(`"${cycle.contentItem.name}" is a web page — the revision re-captures the same URL as the next version. Continue?`)) {
              post({});
            }
          } else {
            setPicking(true);
          }
        }}
        title="Submit the reworked file as the next version of this content"
      >
        {busy ? 'Submitting…' : 'Submit revised version'}
      </button>
      <span style={{ fontSize: 12.5, color: '#666', marginLeft: 10 }}>
        {isWebpage
          ? 'Re-captures the same URL as the next version.'
          : cycle.contentType === 'video' || cycle.contentType === 'image'
            ? 'Pick the reworked file — and update the working material folder by hand (see the note after submitting).'
            : startFromWorkingFileHint(cycle.contentType) || 'Pick the reworked file — it continues this content’s version history.'}
      </span>
      {msg && (
        <div role="alert" style={{ marginTop: 6, fontSize: 13, color: ok ? '#1E7A45' : '#dc3545' }}>{msg}</div>
      )}
      {picking && !busy && (
        <SubmissionQueueModal
          getAuthHeaders={getAuthHeaders}
          onPick={pickFile}
          onClose={() => setPicking(false)}
        />
      )}
      {pendingPdf && !busy && (
        <CompanionDecisionModal
          contentName={cycle.contentItem.name}
          getAuthHeaders={getAuthHeaders}
          onDecide={decideCompanion}
          onClose={() => setPendingPdf(null)}
        />
      )}
    </div>
  );
};

export default SubmitRevisionControl;
